import AddIcon from '@mui/icons-material/Add';
import {
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import type { LopendeFase, Voordracht, VoordrachtDetails } from '../../api/typen.ts';
import { useLopendeVoordrachten, useVoordrachtDetails } from '../../api/voordrachten.ts';
import { Laadstatus } from '../../componenten/Laadstatus.tsx';
import { Pagina } from '../../componenten/Pagina.tsx';
import { VoordrachtDialoog } from '../../componenten/VoordrachtDialoog.tsx';
import { datum, volledigeNaam } from '../../helpers/formatteer.ts';
import { lopendeFaseLabels } from '../../helpers/labels.ts';

const fases: LopendeFase[] = ['voorbereiding', 'voorgesteld', 'gesprek', 'aanbod'];

function Kolom({
  fase,
  items,
}: {
  fase: LopendeFase;
  items: { voordracht: Voordracht; details: VoordrachtDetails | undefined }[];
}) {
  const navigeer = useNavigate();
  return (
    <Paper sx={{ p: 1.5, bgcolor: 'grey.50', height: '100%' }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {lopendeFaseLabels[fase]}
        </Typography>
        <Chip label={items.length} size="small" />
      </Stack>
      <Stack spacing={1}>
        {items.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            Geen voordrachten
          </Typography>
        )}
        {items.map(({ voordracht, details }) => (
          <Card key={voordracht.id}>
            <CardActionArea onClick={() => void navigeer(`/voordrachten/${voordracht.id}`)}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                {details ? (
                  <>
                    <Typography variant="subtitle2">{volledigeNaam(details.kandidaat)}</Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {details.vacature.titel}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Skeleton width="70%" />
                    <Skeleton width="90%" />
                  </>
                )}
                <Typography variant="caption" color="text.secondary">
                  Gewijzigd {datum(voordracht.gewijzigdOp)}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Stack>
    </Paper>
  );
}

function Pijplijn({ voordrachten }: { voordrachten: readonly Voordracht[] }) {
  const { details } = useVoordrachtDetails(voordrachten);
  const perFase = new Map<LopendeFase, { voordracht: Voordracht; details: VoordrachtDetails | undefined }[]>(
    fases.map((fase) => [fase, []]),
  );
  voordrachten.forEach((voordracht, index) => {
    if (voordracht.fase !== 'afgerond') {
      perFase.get(voordracht.fase)?.push({ voordracht, details: details[index] });
    }
  });
  return (
    <Grid container spacing={2}>
      {fases.map((fase) => (
        <Grid key={fase} size={{ xs: 12, sm: 6, lg: 3 }}>
          <Kolom fase={fase} items={perFase.get(fase) ?? []} />
        </Grid>
      ))}
    </Grid>
  );
}

/**
 * De lopende voordrachten als pijplijn met een kolom per fase.
 * @returns De pagina.
 */
export function VoordrachtenPagina() {
  const voordrachten = useLopendeVoordrachten(undefined);
  const [voordragen, setVoordragen] = useState(false);
  const navigeer = useNavigate();

  return (
    <Pagina
      titel="Voordrachten"
      ondertitel="Alle lopende voordrachten, per fase. Afgeronde voordrachten vind je bij de kandidaat of de vacature."
      acties={
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setVoordragen(true)}>
          Voordragen
        </Button>
      }
    >
      <Laadstatus query={voordrachten}>{(lijst) => <Pijplijn voordrachten={lijst} />}</Laadstatus>
      <VoordrachtDialoog
        open={voordragen}
        onSluit={() => setVoordragen(false)}
        onGemaakt={(voordracht) => void navigeer(`/voordrachten/${voordracht.id}`)}
      />
    </Pagina>
  );
}
